import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { FeatureInfoPanelData, FeatureInfoPanelRow } from './visor.types';

interface IrrigatorParcelResponse {
  parcel?: string | null;
  cadastralReference?: string | null;
  hanegadas?: number | null;
  cuartones?: number | null;
  brazas?: number | null;
  municipality?: string | null;
  takeId?: number | null;
  takeName?: string | null;
  projection?: string | null;
  extent?: number[] | null;
}

export interface IrrigatorParcel {
  id: string;
  cadastralReference: string;
  panelData: FeatureInfoPanelData;
  extent?: [number, number, number, number];
}

export type IrrigatorParcelsResult =
  | { kind: 'ready'; parcels: IrrigatorParcel[] }
  | { kind: 'unauthorized' }
  | { kind: 'unavailable' };

@Injectable({ providedIn: 'root' })
export class IrrigatorParcelsService {
  async getCurrentParcels(headers: Record<string, string>): Promise<IrrigatorParcelsResult> {
    try {
      const response = await fetch(`${environment.apiWmsUrl}/irrigators/parcels`, { headers });

      if (response.status === 401 || response.status === 403) {
        return { kind: 'unauthorized' };
      }

      if (!response.ok) {
        return { kind: 'unavailable' };
      }

      const rawParcels = await response.json() as IrrigatorParcelResponse[];

      return {
        kind: 'ready',
        parcels: rawParcels.map((parcel, index) => this.createParcel(parcel, index))
      };
    } catch {
      return { kind: 'unavailable' };
    }
  }

  private createParcel(parcel: IrrigatorParcelResponse, index: number): IrrigatorParcel {
    const cadastralReference = this.getTrimmedText(parcel.cadastralReference) ||
      this.getTrimmedText(parcel.parcel) ||
      `Parcela ${index + 1}`;

    return {
      id: `${cadastralReference}-${index}`,
      cadastralReference,
      panelData: this.createPanelData(parcel, cadastralReference),
      extent: parcel.projection === 'EPSG:3857' && this.isValidExtent(parcel.extent)
        ? parcel.extent
        : undefined
    };
  }

  private createPanelData(parcel: IrrigatorParcelResponse, cadastralReference: string): FeatureInfoPanelData {
    const rows: FeatureInfoPanelRow[] = [];
    const surfaceLines = [
      this.formatMeasuredValue(parcel.hanegadas, 'hanegadas'),
      this.formatMeasuredValue(parcel.cuartones, 'cuartones'),
      this.formatMeasuredValue(parcel.brazas, 'brazas')
    ].filter((value): value is string => Boolean(value));
    const take = this.getTrimmedText(parcel.takeName) ||
      (parcel.takeId === undefined || parcel.takeId === null ? '' : `Toma nº${parcel.takeId}`);

    if (surfaceLines.length) {
      rows.push({
        label: 'Superficie',
        value: surfaceLines[0],
        valueLines: surfaceLines,
        kind: 'surface'
      });
    }

    if (take) {
      rows.push({
        label: 'Toma asociada',
        value: take,
        kind: 'take'
      });
    }

    return {
      title: 'Mi parcela',
      cadastralReference,
      rows,
      profile: 'irrigators'
    };
  }

  private formatMeasuredValue(value: number | null | undefined, unit: string): string {
    if (value === undefined || value === null || value === 0) {
      return '';
    }

    return `${this.formatNumber(value)} ${unit}`;
  }

  private formatNumber(value: number): string {
    return Number.isInteger(value)
      ? String(value)
      : new Intl.NumberFormat('es-ES', { maximumFractionDigits: 3 }).format(value);
  }

  private getTrimmedText(value: string | null | undefined): string {
    return value?.trim() ?? '';
  }

  private isValidExtent(extent: number[] | null | undefined): extent is [number, number, number, number] {
    return Array.isArray(extent) &&
      extent.length === 4 &&
      extent.every((value) => Number.isFinite(value)) &&
      extent[0] <= extent[2] &&
      extent[1] <= extent[3];
  }
}
